'use client';

import { c as _c } from "react/compiler-runtime";
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import CheckIcon from '@mui/icons-material/Check';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { hasCRUDError } from '@/app/_shared/utils';
import { UserRole } from '@/app/_shared/enums';
import { signOut } from 'next-auth/react';
import { type FC, useState, useTransition } from 'react';
import { switchTestRole } from '@/app/actions';
interface RoleSwitcherProps {
  currentRole: string;
  env: 'development' | 'production' | 'test';
  jmtAccess: boolean;
  qmtAccess: boolean;
}
const roleConfig = {
  [UserRole.ADMIN]: {
    label: 'Admin',
    icon: AdminPanelSettingsIcon
  },
  [UserRole.USER]: {
    label: 'User',
    icon: PersonIcon
  },
  [UserRole.GUEST]: {
    label: 'Guest',
    icon: PersonOffIcon
  },
  [UserRole.SITE_MANAGER]: {
    label: 'Site Manager',
    icon: SupervisorAccountIcon
  }
};
export const RoleSwitcher: FC<RoleSwitcherProps> = t0 => {
  const $ = _c(53);
  const {
    currentRole,
    env,
    jmtAccess,
    qmtAccess
  } = t0;
  const [anchorEl, setAnchorEl] = useState(null);
  const [isPending, startTransition] = useTransition();
  const open = Boolean(anchorEl);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = function handleClick(event) {
      setAnchorEl(event.currentTarget);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleClick = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = function handleClose() {
      setAnchorEl(null);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const handleClose = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = function applySwitch(role, newJmt, newQmt) {
      handleClose();
      startTransition(async () => {
        const result = await switchTestRole(role, newJmt, newQmt);
        if (hasCRUDError(result)) {
          return;
        }
        await signOut({
          callbackUrl: "/"
        });
      });
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const applySwitch = t3;
  let t4;
  if ($[3] !== currentRole || $[4] !== jmtAccess || $[5] !== qmtAccess) {
    t4 = async function handleRoleSwitch(role_0) {
      if (role_0 === currentRole) {
        handleClose();
        return;
      }
      applySwitch(role_0 as UserRole, jmtAccess, qmtAccess);
    };
    $[3] = currentRole;
    $[4] = jmtAccess;
    $[5] = qmtAccess;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const handleRoleSwitch = t4;
  let t5;
  if ($[7] !== currentRole || $[8] !== jmtAccess || $[9] !== qmtAccess) {
    t5 = function handleAccessToggle(flag) {
      const newJmt_0 = flag === "jmt" ? !jmtAccess : jmtAccess;
      const newQmt_0 = flag === "qmt" ? !qmtAccess : qmtAccess;
      applySwitch(currentRole as UserRole, newJmt_0, newQmt_0);
    };
    $[7] = currentRole;
    $[8] = jmtAccess;
    $[9] = qmtAccess;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const handleAccessToggle = t5;
  const t6 = env === "test" ? "Testing" : "Development";
  let t7;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      fontWeight: "bold",
      textTransform: "uppercase",
      letterSpacing: 1,
      cursor: "pointer",
      animation: "pulse 2s infinite",
      "@keyframes pulse": {
        "0%": {
          opacity: 1
        },
        "50%": {
          opacity: 0.7
        },
        "100%": {
          opacity: 1
        }
      },
      "&:hover": {
        opacity: 0.9
      }
    };
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== isPending || $[13] !== t6) {
    t8 = <Chip label={t6} color="warning" variant="filled" size="small" onClick={handleClick} disabled={isPending} sx={t7} />;
    $[12] = isPending;
    $[13] = t6;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t10;
  let t9;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      vertical: "bottom",
      horizontal: "left"
    };
    t10 = {
      vertical: "top",
      horizontal: "left"
    };
    $[15] = t10;
    $[16] = t9;
  } else {
    t10 = $[15];
    t9 = $[16];
  }
  let t11;
  let t12;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Typography variant="caption" sx={{
      px: 2,
      py: 0.5,
      display: "block",
      color: "text.secondary"
    }}>Role</Typography>;
    t12 = Object.entries(roleConfig);
    $[17] = t11;
    $[18] = t12;
  } else {
    t11 = $[17];
    t12 = $[18];
  }
  let t13;
  if ($[19] !== currentRole || $[20] !== handleRoleSwitch) {
    t13 = t12.map(t14 => {
      const [role_1, config] = t14;
      const Icon = config.icon;
      const isActive = currentRole === role_1;
      return <MenuItem key={role_1} onClick={() => handleRoleSwitch(role_1 as UserRole)} selected={isActive}><ListItemIcon><Icon fontSize="small" /></ListItemIcon><ListItemText>{config.label}</ListItemText>{isActive && <ListItemIcon><CheckIcon fontSize="small" /></ListItemIcon>}</MenuItem>;
    });
    $[19] = currentRole;
    $[20] = handleRoleSwitch;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <Divider />;
    $[22] = t14;
  } else {
    t14 = $[22];
  }
  let t15;
  if ($[23] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <Typography variant="caption" sx={{
      px: 2,
      py: 0.5,
      display: "block",
      color: "text.secondary"
    }}>Access</Typography>;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  let t16;
  if ($[24] !== handleAccessToggle) {
    t16 = () => handleAccessToggle("jmt");
    $[24] = handleAccessToggle;
    $[25] = t16;
  } else {
    t16 = $[25];
  }
  let t17;
  if ($[26] !== jmtAccess) {
    t17 = jmtAccess && <CheckIcon fontSize="small" />;
    $[26] = jmtAccess;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== t17) {
    t18 = <ListItemIcon>{t17}</ListItemIcon>;
    $[28] = t17;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ListItemText>JMT</ListItemText>;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t16 || $[32] !== t18) {
    t20 = <MenuItem onClick={t16}>{t18}{t19}</MenuItem>;
    $[31] = t16;
    $[32] = t18;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] !== handleAccessToggle) {
    t21 = () => handleAccessToggle("qmt");
    $[34] = handleAccessToggle;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  if ($[36] !== qmtAccess) {
    t22 = qmtAccess && <CheckIcon fontSize="small" />;
    $[36] = qmtAccess;
    $[37] = t22;
  } else {
    t22 = $[37];
  }
  let t23;
  if ($[38] !== t22) {
    t23 = <ListItemIcon>{t22}</ListItemIcon>;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <ListItemText>QMT</ListItemText>;
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== t21 || $[42] !== t23) {
    t25 = <MenuItem onClick={t21}>{t23}{t24}</MenuItem>;
    $[41] = t21;
    $[42] = t23;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  let t26;
  if ($[44] !== anchorEl || $[45] !== open || $[46] !== t13 || $[47] !== t20 || $[48] !== t25) {
    t26 = <Menu anchorEl={anchorEl} open={open} onClose={handleClose} anchorOrigin={t9} transformOrigin={t10}>{t11}{t13}{t14}{t15}{t20}{t25}</Menu>;
    $[44] = anchorEl;
    $[45] = open;
    $[46] = t13;
    $[47] = t20;
    $[48] = t25;
    $[49] = t26;
  } else {
    t26 = $[49];
  }
  let t27;
  if ($[50] !== t26 || $[51] !== t8) {
    t27 = <Box>{t8}{t26}</Box>;
    $[50] = t26;
    $[51] = t8;
    $[52] = t27;
  } else {
    t27 = $[52];
  }
  return t27;
};